<template>
  <div class="flow-outer" :class="{ 'flow-outer--custom': content }">
    <div class="landing-new__body">
      <section class="section-block">
        <div class="section-block__head">
          <h2>{{ flow.heading }}</h2>
          <p>{{ flow.description }}</p>
        </div>

        <div class="flow-diagram">
          <div class="flow-col flow-col--source">
            <p class="flow-eyebrow">{{ flow.sourceEyebrow }}</p>
            <h3 class="flow-heading">{{ flow.sourceHeading }}</h3>
            <div class="flow-items">
              <div v-for="item in sourceItems" :key="item.text" class="flow-item">
                <span class="flow-dots">
                  <span
                    v-for="color in item.colors"
                    :key="color"
                    class="flow-dot"
                    :style="{ background: color }"
                  />
                </span>
                {{ item.text }}
              </div>
            </div>
          </div>

          <FlowArrow />

          <div class="flow-col flow-col--core">
            <img class="flow-core__logo" src="../images/DRCT.svg" alt="DRCT" />
            <h3 class="flow-heading">{{ flow.coreHeading }}</h3>
            <div class="flow-pipeline">
              <div v-for="step in pipelineSteps" :key="step.number" class="flow-pipe__step">
                <span class="flow-pipe__n">{{ step.number }}</span>
                <span class="flow-pipe__text">{{ step.text }}</span>
              </div>
            </div>
            <p class="flow-core__note">{{ coreNoteText }}</p>
          </div>

          <FlowArrow />

          <div class="flow-col flow-col--dest">
            <p class="flow-eyebrow">{{ flow.destEyebrow }}</p>
            <h3 class="flow-heading">{{ flow.destHeading }}</h3>
            <div class="flow-items">
              <div v-for="item in destItems" :key="item.text" class="flow-item">
                <span class="flow-dots">
                  <span
                    v-for="color in item.colors"
                    :key="color"
                    class="flow-dot"
                    :style="{ background: color }"
                  />
                </span>
                {{ item.text }}
              </div>
            </div>
          </div>
        </div>

        <div class="step-row">
          <div v-for="step in steps" :key="step.heading" class="step-col">
            <div class="step-icons" aria-hidden="true">
              <svg
                v-for="(icon, iconIndex) in step.icons"
                :key="`${step.heading}-${iconIndex}`"
                class="step-icon"
                width="18"
                height="18"
                viewBox="0 0 256 256"
                fill="#2a8aea"
              >
                <g
                  v-if="icon.type === 'stroke'"
                  fill="none"
                  stroke="#2a8aea"
                  :stroke-width="icon.strokeWidth || 16"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    v-for="(path, pathIndex) in icon.paths"
                    :key="`${step.heading}-${iconIndex}-${pathIndex}`"
                    :d="path"
                  />
                </g>
                <path v-else :d="icon" />
              </svg>
            </div>
            <h3>{{ step.heading }}</h3>
            <p>{{ step.description }}</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import FlowArrow from './FlowArrow.vue';
import { resolveIconReview } from './page-icon-review.js';
import { CARD_ICON_OPTIONS } from './WhatYouGain.vue';

const STEP_ICON_PATHS = [
  'M230.91,172A8,8,0,0,1,228,182.91l-96,56a8,8,0,0,1-8.06,0l-96-56A8,8,0,0,1,36,169.09l92,53.65,92-53.65A8,8,0,0,1,230.91,172ZM220,121.09l-92,53.65L36,121.09A8,8,0,0,0,28,134.91l96,56a8,8,0,0,0,8.06,0l96-56A8,8,0,1,0,220,121.09ZM24,80a8,8,0,0,1,4-6.91l96-56a8,8,0,0,1,8.06,0l96,56a8,8,0,0,1,0,13.82l-96,56a8,8,0,0,1-8.06,0l-96-56A8,8,0,0,1,24,80Zm23.88,0L128,126.74,208.12,80,128,33.26Z',
  'M197.58,129.06,146,110l-19-51.62a15.92,15.92,0,0,0-29.88,0L78,110l-51.62,19a15.92,15.92,0,0,0,0,29.88L78,178l19,51.62a15.92,15.92,0,0,0,29.88,0L146,178l51.62-19a15.92,15.92,0,0,0,0-29.88ZM137,164.22a8,8,0,0,0-4.74,4.74L112,223.85,91.78,169A8,8,0,0,0,87,164.22L32.15,144,87,123.78A8,8,0,0,0,91.78,119L112,64.15,132.22,119a8,8,0,0,0,4.74,4.74L191.85,144ZM144,40a8,8,0,0,1,8-8h16V16a8,8,0,0,1,16,0V32h16a8,8,0,0,1,0,16H184V64a8,8,0,0,1-16,0V48H152A8,8,0,0,1,144,40ZM248,88a8,8,0,0,1-8,8h-8v8a8,8,0,0,1-16,0V96h-8a8,8,0,0,1,0-16h8V72a8,8,0,0,1,16,0v8h8A8,8,0,0,1,248,88Z',
  'M172,76a44,44,0,1,0-44,44A44.05,44.05,0,0,0,172,76Zm-44,28a28,28,0,1,1,28-28A28,28,0,0,1,128,104Zm60,24a44,44,0,1,0,44,44A44.05,44.05,0,0,0,188,128Zm0,72a28,28,0,1,1,28-28A28,28,0,0,1,188,200ZM68,128a44,44,0,1,0,44,44A44.05,44.05,0,0,0,68,128Zm0,72a28,28,0,1,1,28-28A28,28,0,0,1,68,200Z',
  'M178,40c-20.65,0-38.73,8.88-50,23.89C116.73,48.88,98.65,40,78,40a62.07,62.07,0,0,0-62,62c0,70,103.79,126.66,108.21,129a8,8,0,0,0,7.58,0C136.21,228.66,240,172,240,102A62.07,62.07,0,0,0,178,40ZM128,214.8C109.74,204.16,32,155.69,32,102A46.06,46.06,0,0,1,78,56c19.45,0,35.78,10.36,42.6,27a8,8,0,0,0,14.8,0c6.82-16.67,23.15-27,42.6-27a46.06,46.06,0,0,1,46,46C224,155.61,146.24,204.15,128,214.8Z',
];

const SOURCE_DOT_COLORS = ['#2a8aea', '#2a8aea', 'rgba(42,138,234,0.4)'];
const DEST_DOT_COLORS = [['#9370db'], ['#ff9f00'], ['#33c100']];

export default {
  components: { FlowArrow },
  props: {
    iconReview: {
      type: Array,
      default: () => [],
    },
    content: {
      type: Object,
      default: null,
    },
  },
  data() {
    return { STEP_ICON_PATHS };
  },
  computed: {
    defaultFlow() {
      return {
        heading: this.$t('home.flow.heading'),
        description: this.$t('home.flow.description'),
        sourceEyebrow: this.$t('home.flow.sourceEyebrow'),
        sourceHeading: this.$t('home.flow.sourceHeading'),
        sourceItems: [1, 2, 3].map((n) => this.$t(`home.flow.sourceItem${n}`)),
        coreEyebrow: this.$t('home.flow.coreEyebrow'),
        coreHeading: this.$t('home.flow.coreHeading'),
        coreNote: this.$t('home.flow.coreNote'),
        pipelineSteps: ['01', '02', '03'].map((number, index) => ({
          number,
          text: this.$t(`home.flow.pipeline${index + 1}`),
        })),
        destEyebrow: this.$t('home.flow.destEyebrow'),
        destHeading: this.$t('home.flow.destHeading'),
        destItems: [1, 2, 3].map((n) => this.$t(`home.flow.destItem${n}`)),
        steps: [1, 2, 3, 4].map((n) => ({
          heading: this.$t(`home.flow.step${n}Heading`),
          description: this.$t(`home.flow.step${n}Desc`),
        })),
      };
    },
    flow() {
      return { ...this.defaultFlow, ...this.content };
    },
    sourceItems() {
      return this.flow.sourceItems.map((item, index) => ({
        text: item,
        colors: DEST_DOT_COLORS[index % DEST_DOT_COLORS.length],
      }));
    },
    destItems() {
      return this.flow.destItems.map((item, index) => ({
        text: item,
        colors: [SOURCE_DOT_COLORS[index % SOURCE_DOT_COLORS.length]],
      }));
    },
    pipelineSteps() {
      return this.flow.pipelineSteps;
    },
    coreNoteText() {
      return this.flow.coreNoteText || `${this.flow.coreEyebrow}. ${this.flow.coreNote}.`;
    },
    steps() {
      return this.flow.steps.map((step, index) => {
        const fallbackIcon = step.icon || STEP_ICON_PATHS[step.iconIndex ?? index];
        const icon = CARD_ICON_OPTIONS[step.iconKey] || fallbackIcon;
        const iconOptions = (step.iconOptions || []).map(
          (option) => CARD_ICON_OPTIONS[option] || option
        );

        return {
          ...step,
          icons: resolveIconReview(
            step.replaceIconOptions ? iconOptions : [icon, ...iconOptions],
            this.iconReview[index]
          ),
        };
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.flow-outer {
  background-image: linear-gradient(
    135deg,
    rgba(42, 138, 234, 0.15) 0%,
    rgba(42, 138, 234, 0.04) 100%
  );
}

.section-block {
  padding: 80px 0;
}

.section-block__head {
  text-align: center;

  h2 {
    color: #1b2733;
    font-size: 31px;
    font-weight: 700;
    line-height: 1.25;
  }

  p {
    color: rgba(27, 39, 51, 0.7);
    font-size: 14px;
    line-height: 1.6;
    margin: 12px auto 12px;
    max-width: 680px;
  }
}

.flow-diagram {
  display: flex;
  align-items: center;
  margin-top: 64px;
  gap: 0;
}

.flow-col {
  flex: 1;
  padding: 22px;
  border-radius: 8px;
}

.flow-col--source,
.flow-col--dest {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid transparent;
  box-sizing: border-box;
  height: 199.5px;
}

.flow-col--core {
  background: linear-gradient(
    160deg,
    rgba(42, 138, 234, 0.1) 0%,
    rgba(42, 138, 234, 0.18) 50%,
    rgba(42, 138, 234, 0.1) 100%
  );
  border-radius: 8px;
  flex: 1.35;
  margin: -12px 0;
  z-index: 1;
  position: relative;
}

.flow-eyebrow {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: rgba(27, 39, 51, 0.4);
  margin-bottom: 4px;
}

.flow-heading {
  font-size: 15px;
  font-weight: 700;
  color: #1b2733;
  margin-bottom: 16px;
  line-height: 1.3;
}

.flow-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.flow-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1b2733;
  padding: 8px 10px;
  border: 1px solid rgba(27, 39, 51, 0.08);
  border-radius: 3px;
  background: #fff;
}

.flow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.flow-dots {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.flow-core__logo {
  display: block;
  width: 65px;
  height: auto;
  margin: 0 0 14px;
}

.flow-pipeline {
  position: relative;
  padding-left: 22px;
  margin-bottom: 16px;
}

.flow-pipe__step {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 7px 0;

  &::before {
    content: '';
    position: absolute;
    left: -18px;
    top: 17px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    background: #2a8aea;
    box-shadow: 0 0 0 3px rgba(42, 138, 234, 0.15);
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: -18.5px;
    top: 17px;
    bottom: -17px;
    width: 1px;
    background: linear-gradient(180deg, rgba(42, 138, 234, 0.45) 0%, rgba(42, 138, 234, 0.22) 100%);
  }
}

.flow-pipe__n {
  font-size: 10px;
  font-weight: 600;
  color: rgba(27, 39, 51, 0.4);
  flex-shrink: 0;
  line-height: 20px;
  min-width: 16px;
}

.flow-pipe__text {
  font-size: 13px;
  color: #1b2733;
  line-height: 20px;
}

.flow-core__note {
  font-size: 12px;
  color: rgba(27, 39, 51, 0.4);
  padding-top: 4px;
  border-top: 1px solid rgba(42, 138, 234, 0.12);
  line-height: 1.6;
}

.step-row {
  display: flex;
  border-top: 1px solid rgba(27, 39, 51, 0.1);
  margin-top: 88px;
}

.step-col {
  flex: 1;
  padding: 28px 24px 0;
  border-left: 1px solid rgba(27, 39, 51, 0.1);

  &:first-child {
    border-left: none;
    padding-left: 0;
  }

  h3 {
    color: #1b2733;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 10px;
  }

  p {
    color: rgba(27, 39, 51, 0.7);
    font-size: 14px;
    line-height: 1.57;
  }
}

.step-icons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  line-height: 1;
}

.step-icon {
  display: block;
  flex: 0 0 auto;
}

@media (max-width: 1024px) {
  .section-block {
    padding: 60px 0;
  }
}

@media (max-width: 767px) {
  .flow-diagram {
    flex-direction: column;
    align-items: stretch;
  }
  .flow-col--core {
    margin: 0;
  }

  .flow-col--source,
  .flow-col--dest {
    height: auto;
  }

  .step-row {
    flex-direction: column;
    border-top: none;
  }
  .step-col {
    border-left: none;
    padding: 32px 0 0;
    text-align: center;
    align-items: center;
    display: flex;
    flex-direction: column;

    &:first-child {
      padding-top: 24px;
    }

    p {
      max-width: 300px;
    }
  }

  .step-icon {
    width: 27px;
    height: 27px;
  }
}

@media (max-width: 413px) {
  .section-block__head h2 {
    font-size: 27px;
  }
}
</style>
